/* tslint:disable:cyclomatic-complexity */
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Input, Output, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { User } from 'pdauth';

import { Api } from '../../../api/api';
import { DataDictionaryApi } from '../../../api/data-dictionary-api';
import { LayerApi } from '../../../api/layer-api';
import { SearchApi } from '../../../api/search-api';
import { util } from '../../../classes/util';
import { environment } from '../../../environment';
import { ESRI } from '../../../esri-loader-util';
import { GisToolsService } from '../../../services/gis-tools.service';
import { IAuthenticationService } from '../../../services/interfaces/IAuthenticationService';
import { LayerService } from '../../../services/layer.service';
import { IColumnIdentifer, IInputType, ILayerColumn, IMapcoreSubLayer, IMCMapView, ITableEditableColumn, MapCoreLayer } from '../../../types';
import { BaseComponent } from '../../base.component';

let Graphic: __esri.GraphicConstructor;

const minSearchTextLength = 3;
const searchDebounceTime = 500;
const sideIndicators = { even: 1, odd: 2 };

/* tslint:disable:template-cyclomatic-complexity */
@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-record-editor',
    styleUrls: ['../../sharedStyles/base.scss', './record-editor.component.scss'],
    templateUrl: './record-editor.component.html'
})
export class RecordEditorComponent extends BaseComponent {
    @ViewChild('inputs') private readonly inputsRef: ElementRef<HTMLDivElement>;
    @ViewChild('photo') private readonly photoRef: ElementRef<HTMLInputElement>;

    @Input()
    set active(value: boolean) {
        this._active = value;
        if (!this._active) {
            this.deactivate();
        }
    }
    get active() { return this._active; }
    private _active = false;

    @Input()
    mapViewRef: IMCMapView;

    @Input()
    highlightColor: string = 'rgba(125, 247, 229, 0.75)';
    @Input()
    highlightMarkerSize: number = 16;
    @Input()
    highlightLineColor: string = 'rgba(8, 120, 103, 0.90)';
    @Input()
    highlightLineWidth: number = 1;

    esriLayerId: string;
    objectId: number;

    loading = false;
    saving: string;
    newOnSave = false;
    selectingFromMap = false;
    userPermissions: string[] = [];
    user: User;
    availableCababilities: string[] = [];
    columnIdentifiers: IColumnIdentifer[];
    currentLayer: MapCoreLayer;
    currentFeature: { sourceLayer: IMapcoreSubLayer; attributes: any };
    columns: ILayerColumn[];
    editableColumns: ITableEditableColumn[];
    recordColumns: IRecordColumn[];
    specialRecordColumns: ISpecialRecordColumns = {};
    error: string;
    success: string;

    photoData: string;

    private highlightLayer: __esri.GraphicsLayer;

    crsSuggestions: ICRSSuggestions = {
        counties: [],
        routes: [],
        segments: []
    };
    segmentsMap: { segmentNumber: string; segmentLength: number }[];
    loadingCrsSuggestions: string;

    searchQuery: string;
    bridgeSearchQuery: string = '';
    mpmsSearchQuery: string = '';
    scmSearchQuery: string = '';

    featureSuggestions: ISuggestion[] = [];
    loadingFeatureSuggestions = false;
    subcategoryFeatureSuggestions: ISuggestion[] = [];
    loadingSubcategoryFeatureSuggestions = false;

    get canDelete() { return (this.availableCababilities.includes('DR') || this.availableCababilities.includes('OWN')); }
    get canInsert() { return (this.availableCababilities.includes('IR') || this.availableCababilities.includes('OWN')); }
    get canUpdate() { return (this.availableCababilities.includes('UR') || this.availableCababilities.includes('OWN')); }
    get isNew() { return !this.objectId; }

    get requiredByCRS() {
        let retVal = false;
        if (this.specialRecordColumns
            && this.specialRecordColumns.crs
            && (this.specialRecordColumns.crs.segBegin || this.specialRecordColumns.crs.segEnd)
            && (this.specialRecordColumns.crs.offsetBegin || this.specialRecordColumns.crs.offsetEnd)
            && (((this.specialRecordColumns.crs.segBegin && util.isEmptyUnsetOrWhitespace(this.specialRecordColumns.crs.segBegin.value)) || (this.specialRecordColumns.crs.segEnd && util.isEmptyUnsetOrWhitespace(this.specialRecordColumns.crs.segEnd.value)))
            || ((this.specialRecordColumns.crs.offsetBegin && util.isEmptyUnsetOrWhitespace(this.specialRecordColumns.crs.offsetBegin.value)) || (this.specialRecordColumns.crs.offsetEnd && util.isEmptyUnsetOrWhitespace(this.specialRecordColumns.crs.offsetEnd.value))))) {
            retVal = true;
        }
        return retVal;
    }

    get requiredByLatLong() {
        let retVal = false;
        if (this.specialRecordColumns && this.specialRecordColumns.latLog
            && this.specialRecordColumns.latLog.lat && util.isEmptyUnsetOrWhitespace(this.specialRecordColumns.latLog.lat.value)
            && this.specialRecordColumns.latLog.long && util.isEmptyUnsetOrWhitespace(this.specialRecordColumns.latLog.long.value)) {
            retVal = true;
        }
        return retVal;
    }

    @Output() readonly isComplete = new EventEmitter<{ saved: boolean; shouldClose: boolean; objectId?: number; esriLayerId: string }>();
    @Output() readonly selectingLatLong = new EventEmitter<boolean>();

    constructor(
        @Inject('authenticationService') public authService: IAuthenticationService,
        private readonly layerService: LayerService,
        private readonly layerApi: LayerApi,
        private readonly dataDictionaryApi: DataDictionaryApi,
        private readonly gisToolsService: GisToolsService,
        private readonly searchApi: SearchApi,
        private readonly api: Api,
        private readonly cdr: ChangeDetectorRef,
        _vcr: ViewContainerRef
    ) {
        super(_vcr);
        this.authService.permissions.subscribe(permissions => this.userPermissions = permissions);
        this.authService.user.subscribe(user => this.user = user);

        const bridgeLayer = environment.layers.find(layer => layer.name === 'bridgesAll');
        if (bridgeLayer) {
            this.bridgeSearchQuery = `
                bridge: layers(id: ${bridgeLayer.id}) {
                    data(
                        fieldNames: ["BRKEY", "BRIDGE_ID", "FACILITY", "LOCATION"],
                        query: {
                            field: "TO_CHAR(BRKEY)",
                            operator: "LIKE",
                            value:  $stringIds
                        } 
                    )
                }`;
        }
        const mpmsLayer = environment.layers.find(layer => layer.name === 'mpmsProjectsAll');
        if (mpmsLayer) {
            this.mpmsSearchQuery = `
                mpms: layers(id: ${mpmsLayer.id}) {
                    data(
                        fieldNames: ["PROJECT_ID", "PROJECT_TITLE"],
                        query: {
                            field: "TO_CHAR(PROJECT_ID)",
                            operator: "LIKE",
                            value:  $stringIds
                        } 
                    )
                }`;
        }
        const scmLayer = environment.layers.find(layer => layer.name === 'stormwater');
        if (scmLayer) {
            this.scmSearchQuery = `
                scm: layers(id: ${scmLayer.id}) {
                    data(
                        fieldNames: ["SCM_ID", "SCM_TYPE_CODE_DESC", "SCM_DESCRIPTION"],
                        query: {
                            field: "SCM_ID",
                            operator: "LIKE",
                            value:  $stringIds
                        } 
                    )
                }`;
        }
    }

    @Input()
    activate = async (esriLayerId: string, objectId?: number) => {
        this.active = true;
        this.esriLayerId = esriLayerId;
        this.objectId = objectId;
        this.mapViewRef = await this.setupMapViewRef(null);
        await this.load();
    }

    deactivate() {
        this.esriLayerId = null;
        this.objectId = null;
        this.newOnSave = false;
        this.currentLayer = null;
        this.columnIdentifiers = null;
        this.columns = null;
        this.editableColumns = null;
        this.currentFeature = null;
        this.recordColumns = null;
        this.specialRecordColumns = null;
        this.photoData = null;
        this.error = null;
        this.success = null;
        if (this.highlightLayer) {
            this.mapViewRef.map.remove(this.highlightLayer);
            this.highlightLayer = null;
        }
    }

    cancel() {
        this.isComplete.emit({ saved: false, shouldClose: true, objectId: this.objectId, esriLayerId: this.esriLayerId });
    }

    async load() {
        this.success = null;
        this.loading = true;
        this.cdr.detectChanges();

        Graphic = await ESRI.Graphic;

        this.recordColumns = [];
        this.specialRecordColumns = {};
        this.currentFeature = null;
        if (this.esriLayerId) {
            this.currentLayer = this.layerService.getEsriLayerByUniqueId(this.esriLayerId);
            this.availableCababilities = this.currentLayer.mapcoreProps.permissions.filter(per => (this.userPermissions.includes(per.name) || per.name === 'Owner')).map(per => per.action);
            this.columns = this.currentLayer.mapcoreProps.columns;
            this.editableColumns = this.currentLayer.mapcoreProps.tableEditableColumns;
            this.editableColumns.sort((a, b) => a.displayOrder - b.displayOrder);
            this.setupRecordColumns();
            await this.loadDomainValues();
            this.categoryChange();
            this.subcategoryChange();

            if (this.specialRecordColumns && this.specialRecordColumns.latLog && !this.highlightLayer) {
                if (this.highlightLayer == undefined) {
                    this.highlightLayer = await this.layerService.addGraphicsLayer({ title: 'Record Edit' }, { hideInLegend: true, identifyEnabled: false });
                }
            }

            if (this.objectId) {
                const dbColumns = this.columns.map(c => c.name);
                const data: (string | number)[][] = await this.layerApi.getData(this.currentLayer.mapcoreProps.parentId, dbColumns, { field: 'OBJECTID', operator: '=', value: this.objectId.toString() });
                if (data && data.length) {
                    for (let i = 0; i < data[0].length; i++) {
                        const recordColumn = this.recordColumns.find(rc => rc.columnName.toLowerCase().trim() === dbColumns[i].toLowerCase().trim());
                        if (recordColumn) {
                            recordColumn.value = recordColumn.originalValue = data[0][i];
                            if (recordColumn.inputType === 'date' && typeof recordColumn.value === 'string' && recordColumn.value.includes('T')) {
                                recordColumn.value = recordColumn.originalValue = recordColumn.value.slice(0, recordColumn.value.indexOf('T'));
                            }
                        }
                    }
                    this.updateFields();
                }
                if (this.currentLayer.type === 'map-image' && this.currentLayer.sublayers.length && this.specialRecordColumns.photo) {
                    this.currentFeature = {
                        sourceLayer: this.currentLayer.sublayers.getItemAt(0),
                        attributes: this.recordColumns.map(rc => ({
                            columnName: rc.columnName,
                            displayName: rc.displayName,
                            value: rc.value
                        }))
                    };
                }
            }
            await this.updateCRSSuggestions();
        }
        this.loading = false;
        this.cdr.detectChanges();
    }

    private getInputType(column: ILayerColumn) {
        let retVal: 'number' | 'date' | 'text';
        switch (column.databaseDataType) {
            case 'NUMBER':
                retVal = 'number';
                break;
            case 'DATE':
                retVal = 'date';
                break;
            default:
                retVal = 'text';
                break;
        }
        return retVal;
    }

    private setupRecordColumns() {
        for (const editableColumn of this.editableColumns) {
            const matchingColumn = this.columns.find(col => col.name.toLowerCase().trim() === editableColumn.name.toLowerCase().trim());
            const inputType = this.getInputType(matchingColumn);
            this.recordColumns.push({
                displayName: matchingColumn.alias,
                columnName: matchingColumn.name,
                columnDataLength: matchingColumn.databaseDataLength,
                max: inputType === 'number' ? '9'.repeat(matchingColumn.databaseDataLength) : undefined,
                readonly: this.objectId ? !editableColumn.isEditable : !editableColumn.isInsertable,
                specialInputType: editableColumn.inputType ? editableColumn.inputType.name : null,
                specialInputTypeData: editableColumn.inputType ? editableColumn.inputType : null,
                inputType,
                required: editableColumn.validationType ? editableColumn.validationType.required : false,
                value: null,
                originalValue: null
            });
        }
        const specialRecordColumns = this.recordColumns.filter(rc => rc.specialInputType);
        if (specialRecordColumns.length) {
            this.specialRecordColumns = {};
            for (const specialRecordColumn of specialRecordColumns) {
                switch (specialRecordColumn.specialInputType) {
                    case 'photo-upload':
                        this.specialRecordColumns.photo = specialRecordColumn;
                        break;
                    case 'cty-rt-picker':
                    case 'side-ind':
                        if (!this.specialRecordColumns.crs) {
                            this.specialRecordColumns.crs = {};
                        }
                        switch (specialRecordColumn.specialInputTypeData.attr) {
                            case 'countyNumber':
                                this.specialRecordColumns.crs.county = specialRecordColumn;
                                break;
                            case 'routeNumber':
                                this.specialRecordColumns.crs.route = specialRecordColumn;
                                break;
                            case 'segmentNumberBegin':
                                this.specialRecordColumns.crs.segBegin = specialRecordColumn;
                                break;
                            case 'segmentNumberEnd':
                                this.specialRecordColumns.crs.segEnd = specialRecordColumn;
                                break;
                            case 'segmentOffset':
                            case 'segmentOffsetBegin':
                                this.specialRecordColumns.crs.offsetBegin = specialRecordColumn;
                                break;
                            case 'segmentOffsetEnd':
                                this.specialRecordColumns.crs.offsetEnd = specialRecordColumn;
                                break;
                            case 'sideInd':
                                this.specialRecordColumns.crs.sideInd = specialRecordColumn;
                                break;
                            default:
                                break;
                        }
                        break;
                    case 'lat-long-picker':
                        if (!this.specialRecordColumns.latLog) {
                            this.specialRecordColumns.latLog = {};
                        }
                        if (specialRecordColumn.specialInputTypeData.attr === 'latitude') {
                            this.specialRecordColumns.latLog.lat = specialRecordColumn;
                        } else if (specialRecordColumn.specialInputTypeData.attr === 'longitude') {
                            this.specialRecordColumns.latLog.long = specialRecordColumn;
                        }
                        break;
                    case 'category-feature-picker':
                    case 'subcategory-feature-picker':
                        if (!this.specialRecordColumns.category) {
                            this.specialRecordColumns.category = {};
                        }
                        switch (specialRecordColumn.specialInputTypeData.attr) {
                            case 'category':
                                this.specialRecordColumns.category.category = specialRecordColumn;
                                break;
                            case 'featureId':
                                this.specialRecordColumns.category.featureId = specialRecordColumn;
                                break;
                            case 'subcategory':
                                this.specialRecordColumns.category.subcategory = specialRecordColumn;
                                break;
                            case 'subcategoryFeatureId':
                                this.specialRecordColumns.category.subcategoryFeatureId = specialRecordColumn;
                                break;
                            default:
                                break;
                        }
                        break;
                    default:
                        break;
                }
            }
        }
    }

    private async loadDomainValues() {
        const nonSpecialRecordColumns = this.recordColumns.filter(rc => !rc.specialInputType);
        if (this.specialRecordColumns.category) {
            if (this.specialRecordColumns.category.category) {
                nonSpecialRecordColumns.push(this.specialRecordColumns.category.category);
            }
            if (this.specialRecordColumns.category.subcategory) {
                nonSpecialRecordColumns.push(this.specialRecordColumns.category.subcategory);
            }
        }
        if (nonSpecialRecordColumns.length) {
            const dataDictionaryData = await this.dataDictionaryApi.getColumnsWithDomainValuesByMetaTableId(this.currentLayer.mapcoreProps.metaTableId);
            for (const recordColumn of nonSpecialRecordColumns) {
                const matchingColumn = dataDictionaryData.find(ddd => ddd.columnName.toLowerCase().trim() === recordColumn.columnName.toLowerCase().trim());
                if (matchingColumn && matchingColumn.domainValues && matchingColumn.domainValues.length) {
                    recordColumn.domainValues = matchingColumn.domainValues.map(dv => ({
                        name: `${dv.codeValue} - ${dv.shortDescription}`,
                        value: recordColumn.inputType === 'number' ? parseFloat(dv.codeValue.toString()) : dv.codeValue,
                        description: dv.longDescription
                    } as IDomainValue)).sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
                    if (matchingColumn.columnAlias) {
                        recordColumn.columnAlias = matchingColumn.columnAlias;
                    }
                }
            }
        }
    }

    private updateFields() {
        this.categoryChange();
        this.setLatLongMarker();
        if (this.specialRecordColumns && this.specialRecordColumns.crs) {
            if (this.specialRecordColumns.crs.segBegin && this.specialRecordColumns.crs.offsetBegin) {
                this.updateSegmentMax(this.specialRecordColumns.crs.segBegin, this.specialRecordColumns.crs.offsetBegin);
            }
            if (this.specialRecordColumns.crs.segEnd && this.specialRecordColumns.crs.offsetEnd) {
                this.updateSegmentMax(this.specialRecordColumns.crs.segEnd, this.specialRecordColumns.crs.offsetEnd);
            }
        }
    }

    fileSelected(event: Event) {
        const fileInput = event.target as HTMLInputElement;
        this.photoData = null;
        if (fileInput && fileInput.files && fileInput.files.length) {
            const fileReader = new FileReader();
            fileReader.onload = (e: any) => {
                this.photoData = e.target.result.toString();
                this.cdr.detectChanges();
            };
            fileReader.readAsDataURL(fileInput.files[0]);
        }
        this.cdr.detectChanges();
    }

    startLatLongSelect() {
        const clickHandler = this.mapViewRef.on('click', (event: __esri.ViewClickEvent) => {
            if (this.specialRecordColumns && this.specialRecordColumns.latLog) {
                this.specialRecordColumns.latLog.lat.value = event.mapPoint.latitude;
                this.specialRecordColumns.latLog.long.value = event.mapPoint.longitude;
                this.setCRSFromLatLong().then(() => this.cdr.detectChanges());
            }
            clickHandler.remove();
            this.selectingFromMap = false;
            this.selectingLatLong.emit(false);
            this.setLatLongMarker();
            this.cdr.detectChanges();
        });
        this.selectingFromMap = true;
        this.selectingLatLong.emit(true);
    }

    private setLatLongMarker() {
        if (this.highlightLayer) {
            this.highlightLayer.graphics.removeAll();
            if (this.specialRecordColumns && this.specialRecordColumns.latLog
                && this.specialRecordColumns.latLog.lat && this.specialRecordColumns.latLog.lat.value
                && this.specialRecordColumns.latLog.long && this.specialRecordColumns.latLog.long.value) {
                this.highlightLayer.graphics.add(new Graphic({
                    geometry: {
                        type: 'point',
                        latitude: (<number>this.specialRecordColumns.latLog.lat.value),
                        longitude: (<number>this.specialRecordColumns.latLog.long.value)
                    } as __esri.PointProperties,
                    symbol: {
                        type: 'simple-marker',
                        color: this.highlightColor,
                        size: this.highlightMarkerSize,
                        style: 'circle',
                        outline: {
                            type: 'simple-line',
                            color: this.highlightLineColor,
                            width: this.highlightLineWidth,
                            style: 'solid'
                        } as __esri.SimpleLineSymbolProperties
                    } as __esri.SimpleMarkerSymbolProperties
                }));
            }
        }
    }

    async updateCRSSuggestions(specific: string = null) {
        const suggestionPromises: Promise<void>[] = [];
        if (this.specialRecordColumns && this.specialRecordColumns.crs) {
            if (this.specialRecordColumns.crs.county && !specific) {
                suggestionPromises.push(this.gisToolsService.getAllCounties('countyCode,name')
                    .then((counties: { countyCode: string; name: string }[]) => {
                        this.crsSuggestions.counties = counties.map(county => ({ displayName: county.name, value: county.countyCode }));
                }));
            }
            if (this.specialRecordColumns.crs.route && this.specialRecordColumns.crs.county && (!specific || specific === 'route')) {
                this.crsSuggestions.routes = [];
                if (this.specialRecordColumns.crs.county.value !== null && this.specialRecordColumns.crs.county.value !== '') {
                    suggestionPromises.push(this.gisToolsService.getStateRoutesByCountyJuris(this.specialRecordColumns.crs.county.value.toString(), [1], 'stateRouteNumber,fullName')
                        .then((routes: { stateRouteNumber: string; fullName: string }[]) => {
                            this.crsSuggestions.routes = routes.map(route => ({ displayName: route.fullName, value: route.stateRouteNumber }));
                            this.crsSuggestions.routes.push({ value: 'NON SR' });
                    }));
                }
            }
            if ((this.specialRecordColumns.crs.segBegin || this.specialRecordColumns.crs.segEnd) && this.specialRecordColumns.crs.county && this.specialRecordColumns.crs.route && (!specific || specific === 'seg')) {
                this.crsSuggestions.segments = [];
                this.segmentsMap = null;
                if (this.specialRecordColumns.crs.county.value !== null && this.specialRecordColumns.crs.county.value !== ''
                    && this.specialRecordColumns.crs.route.value !== null && this.specialRecordColumns.crs.route.value !== '') {
                    suggestionPromises.push(this.gisToolsService.getSegmentsByCountyAndRoute(this.specialRecordColumns.crs.county.value.toString(), this.specialRecordColumns.crs.route.value.toString(), 'segmentNumber,segmentLength')
                        .then((segments: { segmentNumber: string; segmentLength: number }[]) => {
                            this.segmentsMap = segments;
                            this.crsSuggestions.segments = segments.map(segment => ({ value: segment.segmentNumber }));
                            this.crsSuggestions.segments.push({ value: 'NON SR' });
                            if (this.specialRecordColumns.crs.segBegin) {
                                if (this.specialRecordColumns.crs.segBegin.value && !this.crsSuggestions.segments.some(seg => seg.value === this.specialRecordColumns.crs.segBegin.value.toString())) {
                                    this.specialRecordColumns.crs.segBegin.value = null;
                                }
                                this.updateSegmentMax(this.specialRecordColumns.crs.segBegin, this.specialRecordColumns.crs.offsetBegin);
                            }
                            if (this.specialRecordColumns.crs.segEnd) {
                                if (this.specialRecordColumns.crs.segEnd.value && !this.crsSuggestions.segments.some(seg => seg.value === this.specialRecordColumns.crs.segEnd.value.toString())) {
                                    this.specialRecordColumns.crs.segEnd.value = null;
                                }
                                this.updateSegmentMax(this.specialRecordColumns.crs.segEnd, this.specialRecordColumns.crs.offsetEnd);
                            }
                            this.cdr.detectChanges();
                    }));
                }
            }
        }
        this.loadingCrsSuggestions = specific || 'all';
        await Promise.all(suggestionPromises);
        this.loadingCrsSuggestions = null;
        this.cdr.detectChanges();
    }

    updateSegmentMax(seg: IRecordColumn, offset: IRecordColumn) {
        if (this.segmentsMap && seg && offset) {
            offset.max = '9'.repeat(offset.columnDataLength);
            offset.maxText = null;
            const segment = this.segmentsMap.find(s => s.segmentNumber === seg.value);
            if (segment && segment.segmentLength) {
                offset.max = segment.segmentLength.toString();
                offset.maxText = `Max offset: ${offset.max}`;
            }
            this.cdr.detectChanges();
        }
    }

    searchFeatures = util.debounce(async () => {
        this.searchFeaturesWork();
    }, searchDebounceTime);
    private async searchFeaturesWork() {
        this.featureSuggestions = [];
        const searchText = this.specialRecordColumns.category.featureId.value;
        if (searchText.toString().length >= minSearchTextLength && this.searchQuery) {
            this.loadingFeatureSuggestions = true;
            const results: ISearchResults = await this.searchApi.getAssetIds(this.searchQuery, searchText.toString());
            this.setSearchResults(results, this.featureSuggestions);
            this.loadingFeatureSuggestions = false;
            this.cdr.detectChanges();
        }
    }

    categoryChange() {
        this.searchQuery = null;
        if (this.specialRecordColumns && this.specialRecordColumns.category) {
            const featureIdCol = this.specialRecordColumns.category.featureId;
            const categoryCol = this.specialRecordColumns.category.category;
            if (featureIdCol && categoryCol && categoryCol.specialInputTypeData.featureCategories) {
                const matchingCategory = categoryCol.value !== null ? categoryCol.specialInputTypeData.featureCategories.find(cat => cat.toLowerCase().trim() === categoryCol.value.toString().toLowerCase().trim()) : null;
                featureIdCol.disabled = !matchingCategory;
                this.setSearchQuery(matchingCategory);
                if (featureIdCol.disabled) {
                    featureIdCol.value = null;
                }
            }
            if (this.specialRecordColumns.category.subcategory) {
                const subcategoryCol = this.specialRecordColumns.category.subcategory;
                subcategoryCol.filteredDomainValues = null;
                if (categoryCol.value) {
                    subcategoryCol.filteredDomainValues = subcategoryCol.domainValues.filter(dv => dv.description === categoryCol.value);
                }
            }
        }
    }

    searchSubcategoryFeatures = util.debounce(async () => {
        this.searchSubcategoryFeaturesWork();
    }, searchDebounceTime);
    private async searchSubcategoryFeaturesWork() {
        this.subcategoryFeatureSuggestions = [];
        const searchText = this.specialRecordColumns.category.subcategoryFeatureId.value;
        if (searchText.toString().length >= minSearchTextLength && this.searchQuery) {
            this.loadingSubcategoryFeatureSuggestions = true;
            const results: ISearchResults = await this.searchApi.getAssetIds(this.searchQuery, searchText.toString());
            this.setSearchResults(results, this.subcategoryFeatureSuggestions);
            this.loadingSubcategoryFeatureSuggestions = false;
            this.cdr.detectChanges();
        }
    }

    subcategoryChange() {
        this.searchQuery = null;
        if (this.specialRecordColumns && this.specialRecordColumns.category) {
            const featureIdCol = this.specialRecordColumns.category.subcategoryFeatureId;
            const categoryCol = this.specialRecordColumns.category.subcategory;
            if (featureIdCol && categoryCol && categoryCol.specialInputTypeData.featureSubcategories) {
                const matchingCategory = categoryCol.value !== null ? categoryCol.specialInputTypeData.featureSubcategories.find(cat => cat.toLowerCase().trim() === categoryCol.value.toString().toLowerCase().trim()) : null;
                featureIdCol.disabled = !matchingCategory;
                this.setSearchQuery(matchingCategory);
                if (featureIdCol.disabled) {
                    featureIdCol.value = null;
                }
            }
        }
    }

    private setSearchQuery(matchingCategory: string) {
        if (matchingCategory) {
            if (matchingCategory.toLowerCase().trim().startsWith('bridge')) {
                this.searchQuery = this.bridgeSearchQuery;
            } else if (matchingCategory.toLowerCase().trim().startsWith('mpms')) {
                this.searchQuery = this.mpmsSearchQuery;
            } else if (matchingCategory.toLowerCase().trim().startsWith('semp')) {
                this.searchQuery = this.scmSearchQuery;
            }
        }
    }

    private distinctResults(results: (string | number)[][]) {
        const retVal: (string | number)[][] = [];
        for (const result of results) {
            if (!retVal.find(r => r.join() === result.join())) {
                retVal.push(result);
            }
        }
        return retVal;
    }

    private setSearchResults(results: ISearchResults, suggestionsList: ISuggestion[]) {
        if (results) {
            if (results.bridge && results.bridge[0].data.length) {
                for (const result of this.distinctResults(results.bridge[0].data)) {
                    suggestionsList.push({
                        displayName: `BRKEY: ${result[0]}, BRIDGE ID: ${result[1]}, ${result[2]} ${result[3]}`,
                        value: result[0].toString()
                    });
                }
            }
            if (results.mpms && results.mpms[0].data.length) {
                for (const result of this.distinctResults(results.mpms[0].data)) {
                    suggestionsList.push({
                        displayName: `MPMS PROJECT: ${result[0]}, ${result[1]}`,
                        value: result[0].toString()
                    });
                }
            }
            if (results.scm && results.scm[0].data.length) {
                for (const result of this.distinctResults(results.scm[0].data)) {
                    suggestionsList.push({
                        displayName: `${result[0]} (${result[1]}), ${result[2]}`,
                        value: result[0].toString()
                    });
                }
            }
        }
    }

    async save(asNew = false) {
        this.error = null;
        this.success = null;
        if (this.validateInputs()) {
            if (this.isNew || asNew) {
                // Set User
                for (const column of this.recordColumns.filter(rc => rc.specialInputType === 'current-user')) {
                    if (column.specialInputTypeData.attr.toLowerCase() === 'username') {
                        column.value = this.user.emailAddress ? this.user.emailAddress.toLowerCase().endsWith('@pa.gov') ? this.user.emailAddress.split('@')[0] : this.user.emailAddress : '';
                    } else if (column.specialInputTypeData.attr.toLowerCase() === 'userid') {
                        column.value = this.user.id;
                    } else {
                        console.error('Current User column not implemented for attribute.', column.specialInputTypeData);
                    }
                }
                // Set Timestamp
                const currentDate = new Date();
                for (const column of this.recordColumns.filter(rc => rc.specialInputType === 'created-timestamp')) {
                    if (column.specialInputTypeData.attr.toLowerCase() === 'createdtimestamp') {
                        column.value = currentDate.valueOf();
                    } else if (column.specialInputTypeData.attr.toLowerCase() === 'createdtimestampyear') {
                        column.value = currentDate.getFullYear().toString();
                    } else {
                        console.error('Created Timestamp column not implemented for attribute.', column.specialInputTypeData);
                    }
                }
            }
            // Set Side Indicator
            this.updateSideInd();
            // Set Lat/Long
            await this.setLatLongFromCRS();
            this.cdr.detectChanges();
            // Re-validate after setting fields
            if (this.validateInputs()) {
                const changedColumns = this.recordColumns.filter(rc => asNew || rc.value !== rc.originalValue);
                if (changedColumns.length) {
                    const formData = new FormData();
                    let removeColumn: IColumnIdentifer;
                    if (this.isNew || asNew) {
                        if (!this.columnIdentifiers) {
                            this.columnIdentifiers = await this.gisToolsService.getAllColumnIdentifiers('name, id');
                        }
                        removeColumn = this.columnIdentifiers.find(ci => ci.name.toLowerCase() === 'objectid');
                    }
                    for (const column of changedColumns) {
                        if (column.specialInputType === 'photo-upload' && this.photoRef) {
                            formData.append(column.columnName, this.photoRef.nativeElement.files[0], column.value.toString());
                        } else {
                            let shouldAdd = true;
                            if (removeColumn) {
                                let match = this.columns.find(c => c.columnIdentifierId === removeColumn.id);
                                if (!match) {
                                    match = this.columns.find(c => c.name.toLowerCase() === removeColumn.name.toLowerCase());
                                }
                                if (match) {
                                    shouldAdd = !(column.columnName.toLowerCase() === match.name.toLowerCase());
                                }
                            }
                            if (shouldAdd) {
                                formData.append(column.columnName, this.formatFormValue(column));
                            }
                        }
                    }
                    try {
                        this.saving = 'Saving';
                        const saveResponse = await this.api.http2.post<number>(`recordEditor/${environment.appId}/${this.currentLayer.mapcoreProps.parentId}${!asNew && !this.isNew ? `/${this.objectId}` : ''}`, formData);
                        if (saveResponse) {
                            this.isComplete.emit({ saved: true, shouldClose: !this.newOnSave, objectId: this.objectId, esriLayerId: this.esriLayerId });
                            this.success = 'Save completed successfuly';
                            if (this.newOnSave) {
                                for (const column of changedColumns) {
                                    column.value = column.originalValue;
                                }
                                if (this.specialRecordColumns && this.specialRecordColumns.photo) {
                                    this.photoData = null;
                                }
                            }
                        } else {
                            this.showError('Save was not completed for an unknown reason');
                        }
                    }
                    catch (err) {
                        this.showError(err.error || err.message);
                    }
                    this.saving = null;
                    this.cdr.detectChanges();
                }
            }
        }
    }

    async delete() {
        this.error = null;
        this.success = null;
        if (confirm('Are you sure you want to delete this record? This cannot be undone.')) {
            try {
                this.saving = 'Deleting';
                const deleteResponse = await this.api.http2.delete<boolean>(`recordEditor/${environment.appId}/${this.currentLayer.mapcoreProps.parentId}/${this.objectId}`);
                if (deleteResponse) {
                    this.isComplete.emit({ saved: true, shouldClose: true, objectId: this.objectId, esriLayerId: this.esriLayerId });
                    this.success = 'Delete completed successfuly';
                } else {
                    this.showError('Delete was not completed for an unknown reason');
                }
            }
            catch (err) {
                this.showError(err.error || err.message);
            }
            this.saving = null;
            this.cdr.detectChanges();
        }
    }

    private validateInputs() {
        let retVal = true;
        if (this.inputsRef) {
            const allInputs = this.inputsRef.nativeElement.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('input,textarea,select');
            for (const input of Array.from(allInputs).reverse()) {
                if (!input.reportValidity()) {
                    retVal = false;
                }
            }
        }
        return retVal;
    }

    private showError(message: string) {
        if (message.includes('ORA-')) {
            message = `DB Error: ${util.capitalize(new RegExp(/ORA-\d+: (.+[^)])/g).exec(message)[1])}`;
        }
        this.error = message;
        this.inputsRef.nativeElement.scrollTo(0, 0);
    }

    private formatFormValue(column: IRecordColumn) {
        let retVal = null;
        if (!util.isEmptyUnsetOrWhitespace(column.value)) {
            retVal = column.value.toString();
            if (column.inputType === 'text') {
                //replace all single quotes with two single quotes to meet oracle syntax.
                retVal = retVal.replaceAll("'", "''");
                retVal = `'${retVal}'`;
            } else if (column.inputType === 'date') {
                const date = typeof column.value === 'string' ? new Date(`${column.value}T00:00`) : new Date(column.value);
                retVal = `TO_DATE('${date.getFullYear()}-${this.n2s(date.getMonth() + 1)}-${this.n2s(date.getDate())} ${this.n2s(date.getHours())}:${this.n2s(date.getMinutes())}:${this.n2s(date.getSeconds())}', 'yyyy-MM-dd HH24:mi:ss')`;
            }
        }
        return retVal;
    }

    private n2s(value: number) {
        return value.toString().padStart(2, '0');
    }

    private updateSideInd() {
        if (this.specialRecordColumns && this.specialRecordColumns.crs && this.specialRecordColumns.crs.sideInd) {
            const sideIndCol = this.specialRecordColumns.crs.sideInd;
            const checkAginst = this.recordColumns.find(rc => rc.columnName.toLowerCase().trim() === sideIndCol.specialInputTypeData.segColName.toLowerCase().trim());
            if (checkAginst && checkAginst.value) {
                sideIndCol.value = util.isEven(checkAginst.value) ? sideIndicators.even : sideIndicators.odd;
            }
        }
    }

    private async setLatLongFromCRS() {
        if (this.specialRecordColumns && this.specialRecordColumns.crs && this.specialRecordColumns.latLog
            && this.specialRecordColumns.crs.county && this.specialRecordColumns.crs.county.value
            && this.specialRecordColumns.crs.route && this.specialRecordColumns.crs.route.value
            && ((this.specialRecordColumns.crs.segBegin && this.specialRecordColumns.crs.segBegin.value)
                || (this.specialRecordColumns.crs.segEnd && this.specialRecordColumns.crs.segEnd.value))
            && ((this.specialRecordColumns.crs.offsetBegin && !util.isEmptyUnsetOrWhitespace(this.specialRecordColumns.crs.offsetBegin.value))
                || (this.specialRecordColumns.crs.offsetEnd && !util.isEmptyUnsetOrWhitespace(this.specialRecordColumns.crs.offsetEnd.value)))
            && this.specialRecordColumns.latLog.lat && util.isEmptyUnsetOrWhitespace(this.specialRecordColumns.latLog.lat.value)
            && this.specialRecordColumns.latLog.long && util.isEmptyUnsetOrWhitespace(this.specialRecordColumns.latLog.long.value)
            && !this.requiredByCRS) {
            const county = this.specialRecordColumns.crs.county.value;
            const route = this.specialRecordColumns.crs.route.value;
            const seg = this.specialRecordColumns.crs.segBegin ? this.specialRecordColumns.crs.segBegin.value : this.specialRecordColumns.crs.segEnd.value;
            const offset = this.specialRecordColumns.crs.offsetBegin ? this.specialRecordColumns.crs.offsetBegin.value : this.specialRecordColumns.crs.offsetEnd.value;
            const result = await this.gisToolsService.getLatLongForOffset(county, route, seg, offset.toString());
            if (result) {
                this.specialRecordColumns.latLog.lat.value = result.latitude;
                this.specialRecordColumns.latLog.long.value = result.longitude;
            }
        }
    }

    private async setCRSFromLatLong() {
        if (this.specialRecordColumns && this.specialRecordColumns.crs && this.specialRecordColumns.latLog
            && this.specialRecordColumns.crs.county && this.specialRecordColumns.crs.county.value
            && this.specialRecordColumns.crs.route && this.specialRecordColumns.crs.route.value
            && (this.specialRecordColumns.crs.segBegin || this.specialRecordColumns.crs.segEnd)
            && (this.specialRecordColumns.crs.offsetBegin || this.specialRecordColumns.crs.offsetEnd)
            && this.specialRecordColumns.latLog.lat && this.specialRecordColumns.latLog.lat.value
            && this.specialRecordColumns.latLog.long && this.specialRecordColumns.latLog.long.value
            && !this.requiredByLatLong) {
            const county = this.specialRecordColumns.crs.county.value.toString();
            const route = this.specialRecordColumns.crs.route.value.toString();
            const lat = this.specialRecordColumns.latLog.lat.value as number;
            const long = this.specialRecordColumns.latLog.long.value as number;
            const result = await this.gisToolsService.getOffsetForCRSLatLong(lat, long, county, route, 3000, 'segmentNumber,offset,sideIndicator');
            if (result && result.length) {
                if (this.specialRecordColumns.crs.segBegin) {
                    this.specialRecordColumns.crs.segBegin.value = result[0].segmentNumber;
                } else {
                    this.specialRecordColumns.crs.segEnd.value = result[0].segmentNumber;
                }
                if (this.specialRecordColumns.crs.offsetBegin) {
                    this.specialRecordColumns.crs.offsetBegin.value = parseInt(result[0].offset, 10);
                } else {
                    this.specialRecordColumns.crs.offsetEnd.value = parseInt(result[0].offset, 10);
                }
            } else {
                if (this.specialRecordColumns.crs.segBegin) {
                    this.specialRecordColumns.crs.segBegin.value = null;
                } else {
                    this.specialRecordColumns.crs.segEnd.value = null;
                }
                if (this.specialRecordColumns.crs.offsetBegin) {
                    this.specialRecordColumns.crs.offsetBegin.value = null;
                } else {
                    this.specialRecordColumns.crs.offsetEnd.value = null;
                }
            }
        }
    }
}

interface IRecordColumn {
    displayName: string;
    columnName: string;
    columnAlias?: string;
    columnDataLength: number;
    max?: string;
    maxText?: string;
    readonly: boolean;
    disabled?: boolean;
    inputType?: 'number' | 'date' | 'text';
    specialInputType?: string;
    specialInputTypeData?: IInputType;
    required: boolean;
    value: string | number;
    originalValue: string | number;
    domainValues?: IDomainValue[];
    filteredDomainValues?: IDomainValue[];
}

interface IDomainValue {
    value: string | number;
    name: string;
    description: string;
}

interface ISpecialRecordColumns {
    photo?: IRecordColumn;
    crs?: {
        county?: IRecordColumn;
        route?: IRecordColumn;
        segBegin?: IRecordColumn;
        offsetBegin?: IRecordColumn;
        segEnd?: IRecordColumn;
        offsetEnd?: IRecordColumn;
        sideInd?: IRecordColumn;
    };
    latLog?: {
        lat?: IRecordColumn;
        long?: IRecordColumn;
    };
    category?: {
        category?: IRecordColumn;
        featureId?: IRecordColumn;
        subcategory?: IRecordColumn;
        subcategoryFeatureId?: IRecordColumn;
    };
}

interface ISuggestion {
    value: string;
    displayName?: string;
}

interface ICRSSuggestions {
    counties: ISuggestion[];
    routes: ISuggestion[];
    segments: ISuggestion[];
}

interface ISearchResults {
    bridge?: { data: (string | number)[][] }[];
    mpms?: { data: (string | number)[][] }[];
    scm?: { data: (string | number)[][] }[];
}
